// Ported from microsites/womens-conference/src/components/VenueSheet.tsx;
// the venue is a prop (MP's address for the event, lib/facts.ts).
import { useEffect, useRef, useState } from 'react';
import { AppleMark, GoogleMapsMark } from '../../../components/Marks';
import { DEFAULT_MAPS_KEY, loadGoogleMaps } from '../../../components/WhereSheet';
import { art } from '../lib/art';
import type { ConferenceConfig } from '../lib/conference';
import { Sheet, SheetBand } from './Sheet';

/**
 * Where it is, and every way she might want to act on that.
 *
 * The venue used to be a bare link straight into Google Maps, which assumed
 * three things at once: that she wants directions, that she wants them now, and
 * that she uses Google. Plenty of people just want to read the address, or are
 * on an iPhone where Apple Maps is the one that will actually open, or want to
 * paste it into a text to whoever is driving.
 *
 * So the sheet opens on a map of where it is, the apps are choices underneath
 * rather than a destination she gets sent to, and the address sits once at the
 * bottom as the thing to copy. Modelled on the platform's CampusAddressSheet,
 * in this site's own hand.
 */
export function VenueSheet({ venue }: { venue: ConferenceConfig['venue'] }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const v = venue;

  const oneLine = [v.address, v.city].filter(Boolean).join(', ');
  const query = encodeURIComponent(`${v.name}, ${oneLine}`);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(t);
  }, [copied]);

  const row =
    'wm-dotted-rule flex w-full items-center gap-4 bg-bottom bg-repeat-x py-3.5 text-left last:bg-none';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="decoration-wm-sage-500 hover:decoration-wm-cream cursor-pointer underline underline-offset-4"
      >
        {v.name}
      </button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title="Where it is"
        band={
          <SheetBand
            kicker="Where"
            title={v.name}
            media={
              v.lat != null && v.lng != null ? <VenueMap lat={v.lat} lng={v.lng} /> : undefined
            }
          />
        }
      >
        <div className="flex flex-col gap-5 p-6 sm:px-7">
          <div className="flex flex-col">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${query}`}
              target="_blank"
              rel="noreferrer noopener"
              className={`${row} hover:text-wm-sage-950`}
            >
              <AppMark>
                <GoogleMapsMark className="h-5 w-5" />
              </AppMark>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">Open in Google Maps</span>
                <span className="text-wm-sage-500 block text-xs">Opens in a new tab</span>
              </span>
              <Arrow />
            </a>

            {/* maps.apple.com falls through to Google on a device without Apple
                Maps, so offering it costs a dead end nowhere. */}
            <a
              href={`https://maps.apple.com/?q=${query}`}
              target="_blank"
              rel="noreferrer noopener"
              className={`${row} hover:text-wm-sage-950`}
            >
              <AppMark>
                <AppleMark className="text-wm-sage-950 h-5 w-5" />
              </AppMark>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">Open in Apple Maps</span>
                <span className="text-wm-sage-500 block text-xs">Opens in a new tab</span>
              </span>
              <Arrow />
            </a>
          </div>

          {/* The address lives here once, as the thing you copy, so the sheet
              never prints it twice. */}
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard
                .writeText(`${v.name}, ${oneLine}`)
                .then(() => setCopied(true))
                .catch(() => setCopied(false));
            }}
            className="text-wm-sage-600 hover:text-wm-sage-950 mx-auto flex max-w-full cursor-pointer items-center gap-2 rounded-full px-3 py-1.5 text-sm transition hover:scale-105 active:scale-95"
          >
            <span className="truncate">{oneLine}</span>
            <CopyGlyph done={copied} />
            <span className="sr-only" aria-live="polite">
              {copied ? 'Copied' : 'Copy the address'}
            </span>
          </button>
        </div>
      </Sheet>
    </>
  );
}

/**
 * Google's map in this site's colours: paper land, cream roads, sage water,
 * and none of the businesses that would crowd the one pin that matters.
 * Hex literals because the Maps API cannot read CSS variables; they are the
 * wm-* tokens from theme.css.
 */
const MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#f3ecdc' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#6b785b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#faf7ef' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ visibility: 'on' }, { color: '#e2e5d5' }],
  },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#fdfbf5' }] },
  { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#e9dfc8' }] },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#c5cbb4' }],
  },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c5cbb4' }] },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#a4ad90' }],
  },
];

/** A plum teardrop holding a blush bud, so the pin is one of the flowers. */
function venuePin(): google.maps.Icon {
  const svg = `<svg width="40" height="52" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z" fill="#5e3542"/>
    <circle cx="16" cy="15.5" r="8" fill="#fdfbf5"/>
    <circle cx="16" cy="15.5" r="4.2" fill="#e3b7c1"/>
    <path d="M16 12.6c1.4.9 1.8 2.2 1 3.4-.8 1-2.3.9-2.8-.2" fill="none" stroke="#c98da0" stroke-width="1" stroke-linecap="round"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(40, 52),
    anchor: new google.maps.Point(20, 52),
  };
}

/**
 * The venue on a map, across the top of the sheet where the keynote has her
 * photograph. The pin is lifted into the upper part of the frame so the
 * heading along the bottom never sits on it. Two fingers to move it on a
 * phone, so a thumb scrolling the sheet still scrolls the sheet.
 */
function VenueMap({ lat, lng }: { lat: number; lng: number }) {
  const host = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let dead = false;
    // Google calls this, rather than rejecting, when the key refuses the page
    // (a host not on its referrer list), and leaves its own error box behind.
    const w = window as Window & { gm_authFailure?: () => void };
    const prior = w.gm_authFailure;
    w.gm_authFailure = () => {
      prior?.();
      if (!dead) setFailed(true);
    };
    loadGoogleMaps(DEFAULT_MAPS_KEY)
      .then(() => {
        if (dead || !host.current) return;
        const map = new google.maps.Map(host.current, {
          center: { lat, lng },
          zoom: 14,
          disableDefaultUI: true,
          keyboardShortcuts: false,
          clickableIcons: false,
          gestureHandling: 'cooperative',
          styles: MAP_STYLES,
        });
        map.panBy(0, 56);
        new google.maps.Marker({ position: { lat, lng }, map, icon: venuePin() });
      })
      .catch(() => !dead && setFailed(true));
    return () => {
      dead = true;
      w.gm_authFailure = prior;
    };
  }, [lat, lng]);

  // A map that will not load becomes the watercolour the other bands use,
  // not a grey box.
  if (failed) {
    return (
      <img
        src={art('/art/watercolor.png')}
        alt=""
        aria-hidden
        className="block h-64 w-full object-cover sm:h-72"
      />
    );
  }
  return <div ref={host} className="bg-wm-cream block h-64 w-full sm:h-72" />;
}

/** The app's own mark on a cream disc, so the row says which app opens. */
function AppMark({ children }: { children: React.ReactNode }) {
  return (
    <span className="bg-wm-petal border-wm-sage-200 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border">
      {children}
    </span>
  );
}

/** Two sheets of paper, or a tick once the address is on the clipboard. */
function CopyGlyph({ done }: { done: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 16 16"
      className={`h-3.5 w-3.5 shrink-0 ${done ? 'text-wm-plum' : 'text-wm-sage-300'}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {done ? (
        <path d="M3 8.5l3.2 3L13 4.5" />
      ) : (
        <>
          <rect x="5.5" y="5.5" width="7.5" height="7.5" rx="1.6" />
          <path d="M10.5 5.5V4.1A1.6 1.6 0 0 0 8.9 2.5H4.1A1.6 1.6 0 0 0 2.5 4.1v4.8a1.6 1.6 0 0 0 1.6 1.6h1.4" />
        </>
      )}
    </svg>
  );
}

function Arrow() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 16 16"
      className="text-wm-sage-300 h-3.5 w-3.5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  );
}
